import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Timestamp, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebase";
import "./Subscription.css";
import { FaArrowLeft } from "react-icons/fa";

const Subscription = () => {
  const { currentUser, userData, userSettings } = useAuth();

  const navigate = useNavigate();
  
  const [billing, setBilling] = useState("monthly");
  const [showCompare, setShowCompare] = useState(false);
  const [selectedView, setSelectedView] = useState("billing");
  const currentPlanKey = (userSettings?.planKey || "free").toLowerCase();
  const [upgradeButtonClicked, setUpgradeButtonClicked] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");
  
  const annualMultiplier = 10;
  
  const mkPrice = useCallback(
    (monthlyZar) => {
      if (monthlyZar === 0) return { display: "$0", sub: "" };
      if (billing === "monthly") return { display: `$${monthlyZar}`, sub: "/month" };
      return { display: `$${monthlyZar * annualMultiplier}`, sub: "/year" };
    },
    [billing]
  );

  const settingsRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid, "settings", "main");
  }, [currentUser]);

  const startWithPlan = useCallback(
    async (planKey, billing) => {
      setUpgradeButtonClicked(true);
      localStorage.setItem("upgrade_pending_plan", planKey);
      localStorage.setItem("upgrade_pending_billing", billing);
      localStorage.setItem("selected_upgrade_pending_plan", planKey);
      localStorage.setItem("selected_upgrade_pending_billing", billing);
      
      try {
        await updateDoc(settingsRef, {
          upgradePlanKey: null,
          upgradePlan: planKey === "free" ? "free" : "upgrading",
          upgradePlanStatus: planKey === "free" ? "active" : "upgrading",
          upgradeBillingCycle: billing,
          updatedAt: serverTimestamp(),
        });
        
        setUpgradeButtonClicked(false);

        navigate(`/upgrade-checkout?plan=${encodeURIComponent(planKey)}`, { replace: true });
      } catch (err) {
        console.error("❌ Failed to set pending plan:", err);
      }},
    [settingsRef, navigate]
  );
  
    // ✅ Updated Plans (USD) — includes Emails + Forms/Submissions + PAYG overages messaging
  // Assumptions used for add-ons:
  // - Extra emails: $0.005 per email (Free), $0.002 per email (paid plans)
  // - Extra submissions: $0.02 per submission (all plans) — strong margin + discourages abuse
  // - Extra forms: $2 per additional form / month (all plans)
  // - AI credits are INCLUDED per plan; extra credits sold separately at $1 per 5 credits (your model)
  
  const plans = useMemo(() => {
    return [
      {
        key: "free",
        name: "Free (PAYG)",
        price: mkPrice(0),
        note: "Start free, pay only when you exceed the included quotas.",
        bullets: [
          "1 website",
          "1 page",
          "1 custom domain",
          "150MB storage",
          "500MB bandwidth",
          "Shop included (5% platform fee per sale)",
          "Forms: 1 included",
          "Submissions: 500",
          "Basic SEO",
          "Basic analytics",
          "10 credits once-off",
          "Email support",
        ],
        cta: "Get started",
        onClick: () => startWithPlan("free", billing),
      },
      {
        key: "startup",
        name: "Startup",
        price: mkPrice(5),
        note: "Best for a single business website that’s growing.",
        bullets: [
          "1 website",
          "Unlimited pages",
          "1 custom domain",
          "1GB storage",
          "3GB bandwidth",
          "Shop included (5% platform fee per sale)",
          "Forms: 3",
          "Submissions: 1,000",
          "Full SEO",
          "Full analytics",
          "70 AI credits",
          "Branding can be turned off",
          "Priority support",
        ],
        cta: "Choose Startup",
        onClick: () => startWithPlan("startup", billing),
      },
      {
        key: "business",
        name: "Business",
        price: mkPrice(20),
        note: "Best value for serious businesses with higher usage.",
        bullets: [
          "Unlimited websites",
          "Unlimited pages",
          "10 custom domains",
          "10GB storage",
          "10GB bandwidth/",
          "Shop included (5% platform fee per sale)",
          "Forms: 10",
          "Submissions: 5,000",
          "Full SEO",
          "Full analytics",
          "150 AI creditsh",
          "Kairos branding off",
          "Higher priority support",
        ],
        cta: "Go Business",
        onClick: () => startWithPlan("business", billing),
      },
      {
        key: "agency",
        name: "Agency",
        price: mkPrice(50),
        note: "For developers & agencies managing many client sites.",
        bullets: [
          "Unlimited websites",
          "Unlimited pages",
          "Unlimited custom domain",
          "25GB storage",
          "50GB bandwidth",
          "Shop included (5% platform fee per sale)",
          "Forms: Unlimited",
          "Submissions: 20,000",
          "Full SEO",
          "Full analytics",
          "300 AI credits",
          "Kairos branding off",
          "Agent priority support",
          "Best PAYG rates",
        ],
        cta: "Go Agency",
        onClick: () => startWithPlan("agency", billing),
      },
    ];
  }, [mkPrice, startWithPlan, billing]);
  
  // ✅ Compare Rows — matches the plan keys above (Free/Startup/Business/Agency)
  const compareRows = useMemo(() => {
    return [
      {
        label: "Websites included",
        free: "1",
        startup: "1",
        business: "Unlimited",
        agency: "Unlimited",
      },
      {
        label: "Pages included",
        free: "1 page",
        startup: "Unlimited",
        business: "Unlimited",
        agency: "Unlimited",
      },
      {
        label: "Custom domains included",
        free: "1",
        startup: "1",
        business: "10",
        agency: "Unlimited",
      },
      {
        label: "Storage included",
        free: "150MB",
        startup: "1GB",
        business: "10GB",
        agency: "25GB",
      },
      {
        label: "Bandwidth/month included",
        free: "500MB",
        startup: "3GB",
        business: "10GB",
        agency: "50GB",
      },
      {
        label: "Shop / payments",
        free: "Included (5% fee per sale)",
        startup: "Included (5% fee per sale)",
        business: "Included (5% fee per sale)",
        agency: "Included (5% fee per sale)",
      },
      {
        label: "SEO",
        free: "Basic",
        startup: "Full",
        business: "Full",
        agency: "Full",
      },
      {
        label: "Analytics",
        free: "Basic",
        startup: "Full",
        business: "Full",
        agency: "Full",
      },
      {
        label: "AI credits included",
        free: "10 (once-off)",
        startup: "70",
        business: "150",
        agency: "300",
      },
      {
        label: "Forms included",
        free: "1",
        startup: "3",
        business: "10",
        agency: "50",
      },
      {
        label: "Form submissions included",
        free: "500 / month",
        startup: "1,000 / month",
        business: "5,000 / month",
        agency: "20,000 / month",
      },
      {
        label: "Branding",
        free: "Kairos branding ON",
        startup: "Can be turned OFF",
        business: "Kairos branding OFF",
        agency: "Kairos branding OFF",
      },
      {
        label: "Support",
        free: "Email support",
        startup: "Priority support",
        business: "Higher priority support",
        agency: "Agent priority support",
      },
  
      // ✅ Suggested PAYG Add-ons (same across plans, but Free is higher for emails)
      {
        label: "Extra submissions (PAYG)",
        free: "$0.02 / submission",
        startup: "$0.02 / submission",
        business: "$0.02 / submission",
        agency: "$0.02 / submission",
      },
      {
        label: "Extra forms (PAYG)",
        free: "$1 / form / month",
        startup: "$1 / form / month",
        business: "$0.5 / form / month",
        agency: "$0.5 / form / month",
      },
      {
        label: "Extra AI credits",
        free: "$0.2 per credit",
        startup: "$0.2 per 5 credits",
        business: "$0.09 per credit",
        agency: "$0.09 per credit",
      },
    ];
    }, []);

  // Your USD anchor pricing (same as Checkout)
  const PLAN_CATALOG = useMemo(
    () => ({
      free: { name: "Free (PAYG)", monthly: 0, annual: 0, tagline: "Start free, pay only when you exceed quotas." },
      startup: { name: "Startup", monthly: 5, annual: 5 * 10, tagline: "Best for a single business website." },
      business: { name: "Business", monthly: 20, annual: 20 * 10, tagline: "Best value for serious businesses." },
      agency: { name: "Agency", monthly: 50, annual: 50 * 10, tagline: "For developers & agencies." },
    }),
    []
  );

  const planKey = (userSettings?.planKey || "free").toLowerCase();
  const billingCycle = (userSettings?.billingCycle || "monthly").toLowerCase(); // "monthly" | "annual"
  const planStatus = (userSettings?.planStatus || "unselected").toLowerCase(); // active | success | failed | pending | past_due | ...

  const selectedPlan = PLAN_CATALOG[planKey] || PLAN_CATALOG.free;

  const amountUsd = useMemo(() => {
    if (!selectedPlan) return 0;
    return billingCycle === "annual" ? selectedPlan.annual : selectedPlan.monthly;
  }, [selectedPlan, billingCycle]);

  const statusLabel = useMemo(() => {
    if (planKey === "free") return "Free";
    if (planStatus === "active") return "Active";
    if (planStatus === "success") return "Payment verified";
    if (planStatus === "pending" || planStatus === "payment-ongoing") return "Payment pending";
    if (planStatus === "past_due") return "Past due";
    if (planStatus === "failed") return "Payment failed";
    if (planStatus === "unselected") return "Not selected";
    return planStatus;
  }, [planKey, planStatus]);

  const statusTone = useMemo(() => {
    if (planKey === "free") return "neutral";
    if (planStatus === "active") return "good";
    if (planStatus === "success") return "good";
    if (planStatus === "pending" || planStatus === "payment-ongoing") return "warn";
    if (planStatus === "past_due" || planStatus === "failed") return "bad";
    return "neutral";
  }, [planKey, planStatus]);

  const renewalAtText = useMemo(() => {
    const ra = userSettings?.renewalAt;
    if (!ra) return "—";

    // renewalAt might be Firestore Timestamp or string
    let dateObj = null;
    if (ra instanceof Timestamp) dateObj = ra.toDate();
    else if (typeof ra === "string") dateObj = new Date(ra);
    else if (ra?.toDate) dateObj = ra.toDate(); // defensive

    if (!dateObj || isNaN(dateObj.getTime())) return "—";
    return dateObj.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, [userSettings?.renewalAt]);

  const cancelAtPeriodEnd = !!userSettings?.cancelAtPeriodEnd;

  const canceledAtText = useMemo(() => {
    const ca = userSettings?.canceledAt;
    if (!ca) return "—";
    const d = ca?.toDate ? ca.toDate() : new Date(ca);
    if (!d || isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, [userSettings?.canceledAt]);

  const credits = userData?.credits ?? 0;

  const isPaidPlan = planKey !== "free" && amountUsd > 0;

  // Button logic
  const showProceed = planStatus === "success";
  const showPayNow = planStatus === "failed" || planStatus === "past_due";
  const showManage = planStatus === "active" && isPaidPlan;

  const goUpgrade = () => setSelectedView("pricing");
  const goProceed = () => navigate("/payment-outcome", { replace: false });
  const goPayNow = () => navigate(`/checkout?plan=${encodeURIComponent(planKey)}`, { replace: false });
  const goBilling = () => setSelectedView("manage-billing");

  // ✅ Support email (simple mailto)
  const contactSupport = useCallback(() => {
    const to = "developers@bakayise.com";
    const subject = encodeURIComponent("Kairos Billing Support");
    const body = encodeURIComponent(
      `Hi Bakayise Developers,\n\nI need help with my Kairos subscription.\n\n` +
      `Account: ${userData?.email || currentUser?.email || "—"}\n` +
      `Plan: ${planKey}\nStatus: ${planStatus}\n\n` +
      `Describe the issue here:\n`
    );

    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }, [userData?.email, currentUser?.email, planKey, planStatus]);

  const resumeSubscription = useCallback(async () => {
    if (!settingsRef) return;
  
    try {
      setActionBusy(true);
      setActionErr("");
      setActionMsg("Resuming subscription...");
  
      await updateDoc(settingsRef, {
        cancelAtPeriodEnd: false,
        resumedAt: serverTimestamp(),
        cancellationReason: null,
  
        // ✅ bring the plan back to normal billing state
        planStatus: "active",
  
        updatedAt: serverTimestamp(),
      });
  
      setActionMsg("Subscription resumed ✅");
    } catch (e) {
      setActionErr(e?.message || "Could not resume subscription.");
      setActionMsg("");
    } finally {
      setActionBusy(false);
    }
  }, [settingsRef]);

  // ✅ Move views without navigating away
  const openBilling = () => setSelectedView("manage-billing");
  const openCancel = () => setSelectedView("cancel");

  return (
    <div className="profile-view">
      <div className="sub-header">
        <h2>Subscription</h2>
        <p className="sub-subtitle">Manage your plan, renewal, and billing actions.</p>
      </div>

      {selectedView === "billing" &&
        <div className="sub-grid">
          {/* Main card */}
          <div className="checkout-card featured sub-card">
            <div className="sub-top">
              <div>
                <h3 className="sub-title">{selectedPlan?.name || "Free (PAYG)"}</h3>
                <p className="sub-tagline">{selectedPlan?.tagline || "Start free and upgrade anytime."}</p>
              </div>

              <span className={`sub-badge ${statusTone}`}>
                {statusLabel}
              </span>
            </div>

            <div className="sub-metrics">
              <div className="sub-metric">
                <span className="muted">Price</span>
                <span className="sub-metric-val">
                  ${amountUsd}
                  <span className="sub-metric-suffix">{billingCycle === "annual" ? "/year" : "/month"}</span>
                </span>
              </div>

              <div className="sub-metric">
                <span className="muted">Billing cycle</span>
                <span className="sub-metric-val cap">{billingCycle}</span>
              </div>

              <div className="sub-metric">
                <span className="muted">Next renewal</span>
                <span className="sub-metric-val">{isPaidPlan ? renewalAtText : "—"}</span>
              </div>

              <div className="sub-metric">
                <span className="muted">Credits</span>
                <span className="sub-metric-val">{credits}</span>
              </div>
            </div>

            {/* Helpful notes based on state */}
            <div className="sub-note">
              {planKey === "free" && (
                <p className="tiny">
                  You’re on the Free plan. Upgrade anytime to unlock more capacity and features.
                </p>
              )}

              {planStatus === "pending" && (
                <p className="tiny">
                  Your payment is pending. If you were charged but didn’t return to the app, use <b>Proceed</b> in Payment Outcome.
                </p>
              )}

              {planStatus === "success" && (
                <p className="tiny">
                  Payment verified ✅ Click <b>Proceed</b> to activate your plan.
                </p>
              )}

              {(planStatus === "failed" || planStatus === "past_due") && (
                <p className="tiny">
                  We couldn’t confirm your payment. Click <b>Pay now</b> to retry, or return to pricing to choose another plan.
                </p>
              )}

              {planStatus === "active" && isPaidPlan && (
                <p className="tiny">
                  Your plan is active. You’ll be charged in ZAR at renewal time using the USD→ZAR live rate.
                </p>
              )}

              {cancelAtPeriodEnd && (
                <p className="tiny">
                  Cancellation scheduled ✅ Your subscription will end at the end of the current period.
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="sub-actions">
              {showProceed && (
                <button className="primary-btn" type="button" onClick={goProceed}>
                  Proceed
                </button>
              )}

              {showPayNow && (
                <button className="primary-btn" type="button" onClick={goPayNow}>
                  Pay now
                </button>
              )}

              <button className="secondary-btn" type="button" onClick={goUpgrade}>
                Change Plan
              </button>

              {showManage && (
                <button className="secondary-btn" type="button" onClick={goBilling}>
                  Manage billing
                </button>
              )}
            </div>

            <div className="sub-foot">
              <div className="mini-row">
                <span>Plan key</span>
                <span className="muted">{planKey || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Status</span>
                <span className="muted">{planStatus || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Billing email</span>
                <span className="muted">{userSettings?.billingEmail || userData?.email || "—"}</span>
              </div>
            </div>
          </div>

          {/* Secondary card: quick links / safety */}
          <div className="checkout-card sub-side">
            <h3>Quick actions</h3>

            <div className="sub-side-list">
              <button className="sub-side-btn" type="button" onClick={openBilling}>
                Manage billing
              </button>

              <button className="sub-side-btn" type="button" onClick={openCancel}>
                Cancel subscription
              </button>

              <button className="sub-side-btn" type="button" onClick={contactSupport}>
                Contact support
              </button>
            </div>

            <p className="tiny" style={{ marginTop: 12 }}>
              Tip: If a renewal fails, the plan may show <b>Past due</b>. Users can retry payment from here.
            </p>
          </div>
        </div>
      }

      {selectedView === "pricing" && 
        <div className="pricing">
          <button className="close-button" onClick={() => setSelectedView("billing")}>
            <FaArrowLeft /> Close
          </button>
          <h2>
            Our <span className="gradient-text">Pricing</span> Plans
          </h2>

          <div className="billing-toggle-wrap">
            <div className="billing-toggle" role="group" aria-label="Billing period toggle">
              <button
                className={billing === "monthly" ? "billing-btn active" : "billing-btn"}
                onClick={() => setBilling("monthly")}
                type="button"
              >
                Monthly
              </button>
              <button
                className={billing === "annual" ? "billing-btn active" : "billing-btn"}
                onClick={() => setBilling("annual")}
                type="button"
              >
                Annual <span className="billing-badge">2 months free</span>
              </button>
            </div>
            <p className="billing-subtext">
              Annual billing is charged once per year and includes a discount equivalent to 2 months free.
            </p>
          </div>

          <div className="pricing-grid">
            {plans.map((p) => {
              const isCurrent = p.key === currentPlanKey && userSettings?.billingCycle === billing; // matches your plan keys: free/startup/business/agency
              const disableThis = isCurrent; // or: isCurrent && isPlanActive
              let buttonLabel = "";

              if (disableThis && !upgradeButtonClicked) {
                buttonLabel = "Current plan";
              } else if (!disableThis && upgradeButtonClicked) {
                buttonLabel = "Loading...";
              } else {
                buttonLabel = p.cta;
              }

              return (
                <div
                  key={p.key}
                  className={[
                    "price-card",
                    p.featured ? "featured" : "",
                    isCurrent ? "current-plan" : "",
                  ].join(" ")}
                >
                  <div className="price-card-column">
                    <div className="price-card-head">
                      <h3>{p.name}</h3>

                      {isCurrent && (
                        <span className="current-badge">
                          Current plan
                        </span>
                      )}
                    </div>

                    <p className="price">
                      {p.price.display} <span className="price-sub">{p.price.sub}</span>
                    </p>

                    <button
                      onClick={disableThis ? undefined : p.onClick}
                      type="button"
                      disabled={disableThis || upgradeButtonClicked}
                      className={disableThis || upgradeButtonClicked ? "disabled-btn" : ""}
                    >
                      {buttonLabel}
                    </button>

                    <p className="price-note">{p.note}</p>

                    <ul>
                      {p.bullets.map((b, idx) => (
                        <li key={idx}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="compare-wrap">
            <button className="compare-btn" type="button" onClick={() => setShowCompare((s) => !s)}>
              {showCompare ? "Hide plan details" : "Show plan details"}
            </button>

            {showCompare && (
              <div className="compare-table-wrap">
                <div className="compare-table">
                  {/* Desktop header row only (CSS hides it on mobile) */}
                  <div className="compare-row compare-head">
                    <div className="compare-cell feature">Feature</div>
                    <div className="compare-cell">Free</div>
                    <div className="compare-cell">Startup</div>
                    <div className="compare-cell">Business</div>
                    <div className="compare-cell">Agency</div>
                  </div>

                  {/* Rows */}
                  {compareRows.map((r, idx) => (
                    <div className="compare-row" key={idx}>
                      <div className="compare-cell feature">{r.label}</div>

                      {/* ✅ data-label used by CSS to create Webflow-style mobile labels */}
                      <div className="compare-cell" data-label="Free">{r.free}</div>
                      <div className="compare-cell" data-label="Startup">{r.startup}</div>
                      <div className="compare-cell" data-label="Business">{r.business}</div>
                      <div className="compare-cell" data-label="Agency">{r.agency}</div>
                    </div>
                  ))}

                  <p className="compare-footnote">
                    * If you keep pages unlimited on paid plans, you can remove the “Extra pages” PAYG line for those tiers.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      }

      {selectedView === "manage-billing" && (
        <div className="billing-view">
          <button className="close-button" onClick={() => setSelectedView("billing")}>
            <FaArrowLeft /> Back
          </button>

          <div className="checkout-card featured sub-card">
            <h3>Manage billing</h3>
            <p className="tiny">
              View your renewal date, payment status, and actions to keep your plan active.
            </p>

            <div className="sub-metrics" style={{ marginTop: 12 }}>
              <div className="sub-metric">
                <span className="muted">Plan</span>
                <span className="sub-metric-val cap">{selectedPlan?.name || planKey}</span>
              </div>

              <div className="sub-metric">
                <span className="muted">Status</span>
                <span className="sub-metric-val cap">{planStatus}</span>
              </div>

              <div className="sub-metric">
                <span className="muted">Next renewal</span>
                <span className="sub-metric-val">{isPaidPlan ? renewalAtText : "—"}</span>
              </div>

              <div className="sub-metric">
                <span className="muted">Billing email</span>
                <span className="sub-metric-val">{userSettings?.billingEmail || userData?.email || "—"}</span>
              </div>
            </div>

            {(planStatus === "failed" || planStatus === "past_due") && (
              <div className="sub-note" style={{ marginTop: 12 }}>
                <p className="tiny">
                  Your billing is <b>{planStatus}</b>. Click <b>Pay now</b> to retry and keep your plan active.
                </p>
              </div>
            )}

            <div className="sub-actions" style={{ marginTop: 14 }}>
              {(planStatus === "failed" || planStatus === "past_due") && (
                <button className="primary-btn" type="button" onClick={goPayNow}>
                  Pay now
                </button>
              )}

              {planStatus === "success" && (
                <button className="primary-btn" type="button" onClick={goProceed}>
                  Proceed (activate)
                </button>
              )}

              <button className="secondary-btn" type="button" onClick={goUpgrade}>
                Change plan
              </button>

              <button className="secondary-btn" type="button" onClick={contactSupport}>
                Contact support
              </button>
            </div>

            <p className="tiny" style={{ marginTop: 12 }}>
              Note: Kairos is USD-priced but charged via Paystack in ZAR using a live USD→ZAR rate at charge time.
            </p>
          </div>
        </div>
      )}

      {selectedView === "cancel" && (
        <div className="billing-view">
          <button className="close-button" onClick={() => setSelectedView("billing")}>
            <FaArrowLeft /> Back
          </button>

          <div className="checkout-card featured sub-card cancel-card">
            <h3>Cancel subscription</h3>

            {!isPaidPlan ? (
              <div className="sub-note" style={{ marginTop: 12 }}>
                <p className="tiny">
                  You’re currently on the <b>Free</b> plan — there’s nothing to cancel.
                </p>
              </div>
            ) : (
              <>
                <p className="tiny" style={{ marginTop: 8 }}>
                  Cancelling will stop future renewals. Your current access can remain active until the end of your billing period
                  (depending on how you enforce access).
                </p>

                <div className="sub-note" style={{ marginTop: 12 }}>
                  <p className="tiny">
                    Current plan: <b>{selectedPlan?.name}</b> • Renewal: <b>{renewalAtText}</b>
                  </p>
                </div>

                <div className="sub-note" style={{ marginTop: 12 }}>
                  <p className="tiny">
                    Cancel at period end: <b>{cancelAtPeriodEnd ? "Yes" : "No"}</b>
                    {"  "}•{"  "}
                    Canceled at: <b>{canceledAtText}</b>
                  </p>
                </div>

                {actionErr && <p className="error" style={{ marginTop: 10 }}>{actionErr}</p>}
                {actionMsg && <p className="success" style={{ marginTop: 10 }}>{actionMsg}</p>}

                {!cancelConfirm ? (
                  <div className="sub-actions" style={{ marginTop: 14 }}>
                    {!cancelAtPeriodEnd ? (
                      <button
                        className="primary-btn"
                        type="button"
                        onClick={() => setCancelConfirm(true)}
                        disabled={actionBusy}
                      >
                        I want to cancel
                      </button>
                    ) : (
                      <button
                        className="primary-btn"
                        type="button"
                        onClick={resumeSubscription}
                        disabled={actionBusy || !settingsRef}
                      >
                        {actionBusy ? "Resuming..." : "Resume subscription"}
                      </button>
                    )}
                  
                    <button className="secondary-btn" type="button" onClick={contactSupport} disabled={actionBusy}>
                      Contact support
                    </button>
                  </div>
                ) : (
                  <div className="sub-actions" style={{ marginTop: 14 }}>
                    <button
                      className="primary-btn danger"
                      type="button"
                      disabled={actionBusy || !settingsRef}
                      onClick={async () => {
                        if (!settingsRef) return;

                        try {
                          setActionBusy(true);
                          setActionErr("");
                          setActionMsg("Cancelling subscription...");

                          await updateDoc(settingsRef, {
                            cancelAtPeriodEnd: true,
                            canceledAt: serverTimestamp(),
                            cancellationReason: "user_initiated",
                          
                            // Helpful for UI/history
                            cancellationPlanKey: planKey,
                            cancellationBillingCycle: billingCycle,
                            cancellationRenewalAt: userSettings?.renewalAt || null,
                          
                            planStatus: "cancelled",
                            updatedAt: serverTimestamp(),
                          });

                          setActionMsg("Subscription cancellation saved. You can still use your plan until the period ends.");
                          setCancelConfirm(false);
                        } catch (e) {
                          setActionErr(e?.message || "Could not cancel subscription.");
                          setActionMsg("");
                        } finally {
                          setActionBusy(false);
                        }
                      }}
                    >
                      {actionBusy ? "Cancelling..." : "Confirm cancel"}
                    </button>

                    <button
                      className="secondary-btn"
                      type="button"
                      disabled={actionBusy}
                      onClick={() => setCancelConfirm(false)}
                    >
                      Keep subscription
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Subscription;
